import { FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { toORPCError } from '@orpc/client'
import { Trans, useTranslation } from 'react-i18next'
import {
  formatCredits,
  MEETING_NOTES_INSTRUCTIONS_MAX,
  PROJECT_DESCRIPTION_MAX,
} from '@hub/contracts'
import { Slider } from '@/components/ui/slider'
import { useDateLocale } from '@/i18n/dates'
import { orpc } from '@/lib/orpc-client'
import { useProject } from '@/lib/project-context'
import { ProjectIcon } from '@/components/project-icon'
import { PROJECT_ICONS } from '@/lib/project-icons'
import { ICON_MAP } from '@/components/project-icon'
import { Button } from '@/components/ui/button'
import {
  SettingsPage,
  SettingsRow,
  SettingsSection,
  dangerButtonClass,
  settingsInputClass,
} from '@/components/settings/settings-layout'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ProjectGroup {
  organization: { id: string; name: string; role: string }
  projects: unknown[]
  projectCount: number
  projectLimit: number
}

export function ProjectSettingsPage({ projectId }: { projectId: string }) {
  const { project, reload } = useProject()
  const navigate = useNavigate()
  const { t } = useTranslation(['settings', 'common'])
  const { intl } = useDateLocale()
  const [name, setName] = useState(project.name)
  const [description, setDescription] = useState(project.description ?? '')
  const [isSaving, setIsSaving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Transfer state
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([])
  const [targetOrgId, setTargetOrgId] = useState('')
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false)
  const [isTransferring, setIsTransferring] = useState(false)
  const [transferError, setTransferError] = useState<string | null>(null)

  // Delete state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [confirmName, setConfirmName] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Appearance state
  const [selectedIcon, setSelectedIcon] = useState<string | null>(project.icon)
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(project.avatarUrl)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Credit cap (owner only, D6) — the project's own ceiling inside the org budget.
  // Slider value: 0 = no cap, else the cap in credits (the org limit is the range).
  const [creditCap, setCreditCap] = useState<number>(project.creditCap ?? 0)
  const [orgLimit, setOrgLimit] = useState<number | null>(null)
  const [isSavingCap, setIsSavingCap] = useState(false)
  const [capError, setCapError] = useState<string | null>(null)
  const [capNotice, setCapNotice] = useState<string | null>(null)

  const isOwner = project.role === 'owner'
  const canEdit = isOwner || project.role === 'admin'

  useEffect(() => {
    setName(project.name)
    setDescription(project.description ?? '')
    setSelectedIcon(project.icon)
    setCurrentAvatarUrl(project.avatarUrl)
    setCreditCap(project.creditCap ?? 0)
  }, [project.name, project.description, project.icon, project.avatarUrl, project.creditCap])

  useEffect(() => {
    if (isOwner) {
      orpc.projects
        .listAllForMe({})
        .then((res) => setProjectGroups(res.groups))
        .catch(() => {})
    }
  }, [isOwner])

  const eligibleOrgs = projectGroups.filter(
    (g) =>
      g.organization.id !== project.organizationId &&
      (g.organization.role === 'owner' || g.organization.role === 'admin') &&
      g.projectCount < g.projectLimit
  )

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canEdit) return

    setIsSaving(true)
    setError(null)
    setNotice(null)

    try {
      await orpc.projects.update({
        projectId,
        name: name.trim(),
        description: description.trim() || null,
      })
      reload()
      window.dispatchEvent(new CustomEvent('project-list-changed'))
      setNotice(t('project.saved'))
    } catch (err) {
      const orpcErr = toORPCError(err)
      setError(orpcErr.message ?? t('project.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    orpc.credits
      .getUsage({ projectId })
      .then((usage) => setOrgLimit(usage.org.limit))
      .catch(() => setOrgLimit(null))
  }, [projectId])

  // The slider spans the organization's budget in round steps; a cap above
  // the budget (limit lowered later) still shows at its own value.
  const capMax = Math.max(orgLimit ?? 0, project.creditCap ?? 0, 1)
  const capStep = niceStep(capMax)
  const capDirty = creditCap !== (project.creditCap ?? 0)

  // Board m5gc #9: how HUB should write this project's meeting notes.
  const [notesInstructions, setNotesInstructions] = useState(project.meetingNotesInstructions ?? '')
  const [isSavingNotes, setIsSavingNotes] = useState(false)
  const [notesNotice, setNotesNotice] = useState<string | null>(null)
  const [notesError, setNotesError] = useState<string | null>(null)
  useEffect(() => {
    setNotesInstructions(project.meetingNotesInstructions ?? '')
  }, [project.meetingNotesInstructions])
  const notesDirty = notesInstructions.trim() !== (project.meetingNotesInstructions ?? '')

  const handleSaveNotesInstructions = async (e: FormEvent) => {
    e.preventDefault()
    if (!canEdit) return
    setIsSavingNotes(true)
    setNotesError(null)
    setNotesNotice(null)
    try {
      await orpc.projects.update({
        projectId,
        meetingNotesInstructions: notesInstructions.trim() || null,
      })
      reload()
      setNotesNotice(t('project.saved'))
    } catch (err) {
      setNotesError(toORPCError(err).message ?? t('project.saveFailed'))
    } finally {
      setIsSavingNotes(false)
    }
  }

  const handleSaveCap = async (e: FormEvent) => {
    e.preventDefault()
    if (!isOwner) return
    const parsed = creditCap <= 0 ? null : Math.round(creditCap)
    setIsSavingCap(true)
    setCapError(null)
    setCapNotice(null)
    try {
      await orpc.projects.update({ projectId, creditCap: parsed })
      reload()
      setCapNotice(t('project.saved'))
    } catch (err) {
      setCapError(toORPCError(err).message ?? t('project.saveFailed'))
    } finally {
      setIsSavingCap(false)
    }
  }

  const handleTransfer = async () => {
    if (!targetOrgId) return

    setIsTransferring(true)
    setTransferError(null)

    try {
      await orpc.projects.transfer({ projectId, targetOrganizationId: targetOrgId })
      setIsTransferDialogOpen(false)
      window.dispatchEvent(new CustomEvent('project-list-changed'))
      reload()
    } catch (err) {
      const orpcErr = toORPCError(err)
      setTransferError(orpcErr.message ?? t('project.transferFailed'))
    } finally {
      setIsTransferring(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    setDeleteError(null)

    try {
      await orpc.projects.delete({ projectId, confirmName })
      window.dispatchEvent(new CustomEvent('project-list-changed'))
      void navigate({ to: '/' })
    } catch (err) {
      const orpcErr = toORPCError(err)
      setDeleteError(orpcErr.message ?? t('project.deleteFailed'))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleIconSelect = async (iconKey: string) => {
    const newIcon = iconKey === selectedIcon ? null : iconKey
    setSelectedIcon(newIcon)
    try {
      await orpc.projects.update({ projectId, icon: newIcon })
      reload()
      window.dispatchEvent(new CustomEvent('project-list-changed'))
    } catch {
      setSelectedIcon(project.icon)
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''

    setAvatarError(null)

    if (file.size > 5 * 1024 * 1024) {
      setAvatarError(t('project.imageTooLarge'))
      return
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAvatarError(t('project.imageInvalidType'))
      return
    }

    setIsUploadingAvatar(true)
    try {
      const reader = new FileReader()
      const base64Data = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const result = await orpc.projects.uploadAvatar({
        projectId,
        fileName: file.name,
        mimeType: file.type as 'image/jpeg' | 'image/png' | 'image/webp',
        base64Data,
      })
      setCurrentAvatarUrl(result.avatarUrl)
      reload()
      window.dispatchEvent(new CustomEvent('project-list-changed'))
    } catch (err) {
      const orpcErr = toORPCError(err)
      setAvatarError(orpcErr.message ?? t('project.uploadFailed'))
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleRemoveAvatar = async () => {
    setAvatarError(null)
    try {
      await orpc.projects.removeAvatar({ projectId })
      setCurrentAvatarUrl(null)
      reload()
      window.dispatchEvent(new CustomEvent('project-list-changed'))
    } catch (err) {
      const orpcErr = toORPCError(err)
      setAvatarError(orpcErr.message ?? t('project.removeImageFailed'))
    }
  }

  return (
    <SettingsPage>
      {/* Appearance */}
      {canEdit && (
        <SettingsSection title={t('project.appearance')}>
          {/* Preview + avatar upload / remove */}
          <div className="flex flex-wrap items-center gap-4">
            <ProjectIcon
              name={project.name}
              icon={selectedIcon}
              avatarUrl={currentAvatarUrl}
              size="lg"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium">{project.name}</p>
              <p className="text-xs text-muted-foreground">
                {currentAvatarUrl
                  ? t('project.projectImage')
                  : selectedIcon
                    ? t('project.selectedIcon')
                    : t('project.defaultAppearance')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarUpload}
              />
              {currentAvatarUrl && (
                <Button type="button" variant="ghost" size="sm" onClick={handleRemoveAvatar}>
                  {t('project.removeImage')}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isUploadingAvatar}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploadingAvatar ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    {t('project.uploading')}
                  </>
                ) : (
                  t('project.uploadImage')
                )}
              </Button>
            </div>
          </div>
          {avatarError && <p className="text-xs text-destructive">{avatarError}</p>}
          <p className="text-xs text-muted-foreground">{t('project.imageHint')}</p>

          {/* Icon picker grid — wraps to the pane width */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              {currentAvatarUrl ? t('project.pickIconWithImage') : t('project.pickIcon')}
            </p>
            <div className="flex flex-wrap gap-1">
              {PROJECT_ICONS.map((iconKey) => {
                const Icon = ICON_MAP[iconKey]
                if (!Icon) return null
                const isSelected = selectedIcon === iconKey
                return (
                  <button
                    key={iconKey}
                    type="button"
                    onClick={() => void handleIconSelect(iconKey)}
                    className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors hover:bg-accent ${
                      isSelected
                        ? 'border-primary ring-2 ring-foreground/30 bg-accent'
                        : 'border-transparent'
                    }`}
                    title={iconKey}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                )
              })}
            </div>
          </div>
        </SettingsSection>
      )}

      <SettingsSection title={t('project.details')}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">{t('project.name')}</span>
            <input
              type="text"
              required
              maxLength={120}
              disabled={!canEdit}
              className={settingsInputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium">{t('project.description')}</span>
            <textarea
              maxLength={PROJECT_DESCRIPTION_MAX}
              rows={4}
              disabled={!canEdit}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none resize-none focus-visible:ring-2 focus-visible:ring-foreground/30 disabled:opacity-60"
              placeholder={t('project.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}

          {canEdit && (
            <div>
              <Button type="submit" disabled={isSaving || !name.trim()}>
                {isSaving ? t('project.saving') : t('project.save')}
              </Button>
            </div>
          )}
        </form>
      </SettingsSection>

      {/* Meeting notes instructions (board m5gc #9) */}
      <SettingsSection
        title={t('project.meetingNotesTitle')}
        description={t('project.meetingNotesBody')}
      >
        <form onSubmit={handleSaveNotesInstructions} className="flex flex-col gap-4">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">{t('project.meetingNotesLabel')}</span>
            <textarea
              maxLength={MEETING_NOTES_INSTRUCTIONS_MAX}
              rows={6}
              disabled={!canEdit}
              data-testid="meeting-notes-instructions"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none resize-y focus-visible:ring-2 focus-visible:ring-foreground/30 disabled:opacity-60"
              placeholder={t('project.meetingNotesPlaceholder')}
              value={notesInstructions}
              onChange={(e) => setNotesInstructions(e.target.value)}
            />
            <span className="block text-xs text-muted-foreground">
              {t('project.meetingNotesHint', {
                count: notesInstructions.length,
                max: MEETING_NOTES_INSTRUCTIONS_MAX,
              })}
            </span>
          </label>

          {notesNotice && <p className="text-sm text-muted-foreground">{notesNotice}</p>}
          {notesError && <p className="text-sm text-destructive">{notesError}</p>}

          {canEdit && (
            <div>
              <Button type="submit" disabled={isSavingNotes || !notesDirty}>
                {isSavingNotes ? t('project.saving') : t('project.save')}
              </Button>
            </div>
          )}
        </form>
      </SettingsSection>

      {/* Credit cap */}
      <SettingsSection title={t('project.creditCapTitle')} description={t('project.creditCapBody')}>
        <form onSubmit={handleSaveCap} className="flex flex-col gap-4">
          <SettingsRow
            label={t('project.creditCap')}
            description={
              <span className="tabular-nums">
                {creditCap <= 0
                  ? t('project.creditCapNone')
                  : t('project.creditCapValue', { credits: formatCredits(creditCap, intl) })}
              </span>
            }
          >
            <div className="w-64 max-w-full space-y-2">
              <Slider
                aria-label={t('project.creditCap')}
                min={0}
                max={capMax}
                step={capStep}
                value={[Math.min(creditCap, capMax)]}
                onValueChange={([v]) => setCreditCap(v ?? 0)}
                disabled={!isOwner || orgLimit === null}
              />
              <div className="flex justify-between gap-2 text-xs text-muted-foreground">
                <span>{t('project.creditCapNone')}</span>
                <span>
                  {orgLimit !== null
                    ? t('project.creditCapOrgLimit', { credits: formatCredits(orgLimit, intl) })
                    : ''}
                </span>
              </div>
            </div>
          </SettingsRow>
          {capNotice && <p className="text-sm text-muted-foreground">{capNotice}</p>}
          {capError && <p className="text-sm text-destructive">{capError}</p>}
          {isOwner ? (
            <div>
              <Button type="submit" variant="outline" disabled={isSavingCap || !capDirty}>
                {isSavingCap ? t('project.saving') : t('project.save')}
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              {t('project.creditCapOwnerOnly')}
            </p>
          )}
        </form>
      </SettingsSection>

      {/* Danger zone: transfer + delete, last in the pane */}
      {isOwner && (
        <SettingsSection tone="danger" title={t('project.dangerZone')}>
          <SettingsRow label={t('project.transferTitle')} description={t('project.transferBody')}>
            {eligibleOrgs.length > 0 ? (
              <>
                <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                  <SelectTrigger className="w-56" aria-label={t('project.targetOrg')}>
                    <SelectValue placeholder={t('project.selectOrgPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleOrgs.map((group) => (
                      <SelectItem key={group.organization.id} value={group.organization.id}>
                        {group.organization.name} ({group.projectCount}/{group.projectLimit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  className={dangerButtonClass}
                  disabled={!targetOrgId}
                  onClick={() => {
                    setTransferError(null)
                    setIsTransferDialogOpen(true)
                  }}
                >
                  {t('project.transfer')}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground italic">{t('project.noEligibleOrgs')}</p>
            )}
          </SettingsRow>

          <SettingsRow label={t('project.deleteTitle')} description={t('project.deleteBody')}>
            <Button
              variant="outline"
              className={dangerButtonClass}
              data-action="delete-project"
              onClick={() => {
                setConfirmName('')
                setDeleteError(null)
                setIsDeleteDialogOpen(true)
              }}
            >
              {t('project.deleteButton')}
            </Button>
          </SettingsRow>
        </SettingsSection>
      )}

      {/* Transfer confirmation dialog */}
      <Dialog open={isTransferDialogOpen} onOpenChange={setIsTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('project.transferTitle')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="project.transferConfirm"
                values={{
                  project: project.name,
                  org:
                    eligibleOrgs.find((g) => g.organization.id === targetOrgId)?.organization
                      .name ?? '',
                }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
          </DialogHeader>
          {transferError && <p className="text-sm text-destructive">{transferError}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsTransferDialogOpen(false)}
              disabled={isTransferring}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button onClick={handleTransfer} disabled={isTransferring}>
              {isTransferring ? t('project.transferring') : t('project.transferSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('project.deleteTitle')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="project.deleteConfirm"
                values={{ project: project.name }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <input
            type="text"
            className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
            placeholder={project.name}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
          />
          {deleteError && <p className="text-sm text-destructive">{deleteError}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={confirmName !== project.name || isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? t('project.deleting') : t('project.deletePermanently')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsPage>
  )
}

/** A round slider step for a budget: ~100 steps, snapped to 1/2/5 × 10ⁿ. */
function niceStep(max: number): number {
  const raw = max / 100
  if (raw <= 1) return 1
  const pow = 10 ** Math.floor(Math.log10(raw))
  const unit = raw / pow
  const snapped = unit >= 5 ? 5 : unit >= 2 ? 2 : 1
  return snapped * pow
}
